/**
 * MechanicComposer.js — The Arcanum Forge
 *
 * Main split-view mechanic composer with persistent live preview.
 * Desktop: 60/40 editor/preview split. Mobile: single column with tab toggle.
 *
 * Layout:
 * - Top bar (fixed, h-14) with name, save/publish, JSON toggle, close
 * - Mini overview bar with section completion pills
 * - Editor (5 collapsible accordion sections)
 * - Preview (ComposedMechanicRenderer + validation + JSON overlay)
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  ChevronDown, CheckCircle2, Circle, Plus,
  Save, Eye, Code, ArrowLeft, Scale,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../../lib/utils';
import { useHomebrewWizard } from '../../../hooks/useHomebrewWizard';
import { validateFull, validateStep, getOverviewStatus } from '../../../utils/homebrewValidation';
import { saveHomebrewContent } from '../../../services/homebrewStorageService';
import { registerComposedMechanic, computeRegistryKey } from '../../../config/composedMechanicRegistry';
import { analyzeBalance } from '../../../services/balanceCheckService';
import MechanicComposerOverview from './MechanicComposerOverview';
import MechanicComposerPreview from './MechanicComposerPreview';
import BasicsSection from './sections/BasicsSection';
import ActionsSection from './sections/ActionsSection';
import TokensSection from './sections/TokensSection';
import PassivesSection from './sections/PassivesSection';
import DisplaySection from './sections/DisplaySection';

// ── Section definitions ──
const SECTIONS = [
  { key: 'basics', number: 1, title: 'Basics', Component: BasicsSection },
  { key: 'actions', number: 2, title: 'Actions', Component: ActionsSection },
  { key: 'tokens', number: 3, title: 'Tokens', Component: TokensSection },
  { key: 'passives', number: 4, title: 'Passives', Component: PassivesSection },
  { key: 'display', number: 5, title: 'Display', Component: DisplaySection },
];

/**
 * EditorSection — Accordion section wrapper.
 * Reuses the exact pattern from LevelOneEditor.js lines 310-372.
 */
function EditorSection({ title, number, status, isExpanded, onToggle, children }) {
  const handleToggle = (e) => {
    e.preventDefault();
    const scrollY = window.scrollY;
    onToggle();
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    });
  };

  return (
    <div className={cn(
      'bg-gradient-to-br from-gray-900 to-black rounded-lg shadow-2xl border transition-all',
      status === 'complete' && 'border-green-500',
      status === 'error' && 'border-red-700',
      status === 'optional' && 'border-gray-700 border-dashed',
      status === 'incomplete' && 'border-gray-700',
    )}>
      <button
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-controls={`section-content-${number}`}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-black/30 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-4">
          <div className={cn(
            'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
            status === 'complete' && 'bg-green-500 text-white',
            status === 'error' && 'bg-red-700 text-white',
            (status === 'optional' || status === 'incomplete') && 'bg-gray-700 text-gray-400',
          )}>
            {number}
          </div>
          <div>
            <h3 className="text-lg text-red-400 font-serif">{title}</h3>
            {status === 'complete' && (
              <p className="text-xs text-green-400">Complete</p>
            )}
            {status === 'optional' && (
              <p className="text-xs text-gray-500">Optional</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {status === 'complete' && (
            <CheckCircle2 className="w-5 h-5 text-green-400" aria-hidden="true" />
          )}
          {status === 'error' && (
            <Circle className="w-5 h-5 text-red-400" aria-hidden="true" />
          )}
          {status === 'optional' && (
            <Plus className="w-5 h-5 text-gray-500" aria-hidden="true" />
          )}
          {status === 'incomplete' && (
            <Circle className="w-5 h-5 text-gray-500" aria-hidden="true" />
          )}
          <ChevronDown className={cn(
            'w-5 h-5 text-gray-400 transition-transform',
            isExpanded && 'rotate-180',
          )} />
        </div>
      </button>

      {isExpanded && (
        <div id={`section-content-${number}`} role="region" aria-label={title} className="px-6 pb-6 pt-2">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Slugify a name string to kebab-case for ID generation.
 */
const slugify = (name) =>
  (name || 'untitled')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

/**
 * @param {object} props
 * @param {function} props.onClose - Called when user closes the composer
 * @param {function} props.onSave - Called after successful save
 * @param {string} props.userId - Firebase auth UID
 * @param {object} [props.editMechanic] - Optional existing mechanic to edit
 */
export default function MechanicComposer({ onClose, onSave, onRequestClose, userId, editMechanic }) {
  const { config, dispatch } = useHomebrewWizard(editMechanic);

  // ── UI state ──
  const [expandedSections, setExpandedSections] = useState([0]); // Start with Basics open
  const [showJson, setShowJson] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mobileView, setMobileView] = useState('edit'); // 'edit' | 'preview'
  const [isDirty, setIsDirty] = useState(false);
  const [balanceReport, setBalanceReport] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState(null);
  const [balanceStale, setBalanceStale] = useState(false);
  const [balanceCredits, setBalanceCredits] = useState(null);

  // ── Section refs for scrolling ──
  const sectionRefs = useRef([]);

  // ── Dirty tracking ──
  const initialConfigRef = useRef(JSON.stringify(config));
  useEffect(() => {
    if (JSON.stringify(config) !== initialConfigRef.current) {
      setIsDirty(true);
      if (balanceReport) {
        setBalanceStale(true);
      }
    }
  }, [config, balanceReport]);

  // ── Unsaved-changes browser guard ──
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── Close handler with dirty guard ──
  const handleClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard?')) return;
    if (onRequestClose) onRequestClose();
    else if (onClose) onClose();
  };

  const toggleSection = useCallback((index) => {
    setExpandedSections(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  }, []);

  const scrollToSection = useCallback((index) => {
    // Expand the section if collapsed
    setExpandedSections(prev =>
      prev.includes(index) ? prev : [...prev, index]
    );
    // Scroll into view after React renders
    requestAnimationFrame(() => {
      sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // ── Validation ──
  const fullValidation = validateFull(config);
  const sectionErrors = {};
  SECTIONS.forEach(s => {
    sectionErrors[s.key] = validateStep(s.key, config).errors;
  });
  const sectionStatuses = getOverviewStatus(config);

  // ── Save Draft ──
  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const id = config.id || `hb_${userId.slice(0, 8)}_${slugify(config.meta?.name)}_${Math.random().toString(36).slice(2, 6)}`;

      // Update config id if first save
      if (!config.id) {
        dispatch({ type: 'SET_ID', value: id });
      }

      await saveHomebrewContent(userId, {
        id,
        type: 'mechanic',
        status: 'draft',
        meta: { ...config.meta, name: config.meta?.name || 'Untitled' },
        data: { ...config, id },
      });

      toast.success('Draft saved');
      setIsDirty(false);
      initialConfigRef.current = JSON.stringify(config);
    } catch (err) {
      console.error('Failed to save draft:', err);
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  // ── Publish ──
  const handlePublish = async () => {
    // Ensure ID exists
    const id = config.id || `hb_${userId.slice(0, 8)}_${slugify(config.meta?.name)}_${Math.random().toString(36).slice(2, 6)}`;
    const publishConfig = { ...config, id };

    // Compute the registry key that renderers will use for lookup
    const registryKey = computeRegistryKey(publishConfig);
    publishConfig.meta = { ...publishConfig.meta, registryKey };

    const validation = validateFull(publishConfig);
    if (!validation.valid) {
      toast.error(`Cannot publish: ${validation.errors[0]}`, { duration: 5000 });
      return;
    }

    setSaving(true);
    try {
      const savedId = await saveHomebrewContent(userId, {
        id,
        type: 'mechanic',
        status: 'ready',
        meta: { ...publishConfig.meta, name: publishConfig.meta?.name || 'Untitled', registryKey },
        data: publishConfig,
      });

      // Register with the key that renderers will look up
      registerComposedMechanic(registryKey || savedId, publishConfig);

      toast.success('Mechanic published!', { icon: '\u2728' });
      setIsDirty(false);
      if (onSave) onSave();
    } catch (err) {
      console.error('Failed to publish mechanic:', err);
      toast.error('Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  const handleBalanceCheck = async () => {
    setBalanceLoading(true);
    setBalanceError(null);
    try {
      const result = await analyzeBalance(config, userId);
      if (result.success) {
        setBalanceReport(result.report);
        setBalanceStale(false);
        setBalanceCredits(result.creditsRemaining);
      } else {
        setBalanceError(result.error);
      }
    } catch (err) {
      setBalanceError('Balance check failed. Please try again.');
    } finally {
      setBalanceLoading(false);
    }
  };

  const displayName = config.meta?.name || 'New Mechanic';

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 12rem)' }}>
      {/* ── Action Bar ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-red-700 rounded-t-lg">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={handleClose}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-gray-300 hover:text-red-400 hover:bg-red-900/30 transition-colors"
            title="Back to Storeroom"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <p className="text-sm text-gray-300 font-serif truncate">{displayName}</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSaveDraft}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-gray-800 text-gray-300 border border-gray-600 hover:border-gray-500 hover:text-white transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span className="hidden sm:inline">Save Draft</span>
          </button>
          <button
            onClick={handlePublish}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-red-900/60 text-red-200 border border-red-700 hover:bg-red-800/60 hover:text-white transition-colors disabled:opacity-50"
          >
            <span>Publish</span>
          </button>
          <button
            onClick={handleBalanceCheck}
            disabled={balanceLoading || saving}
            className={cn(
              'p-1.5 rounded transition-colors',
              balanceReport && !balanceStale
                ? 'bg-green-900/40 text-green-400 border border-green-700'
                : 'text-gray-400 hover:text-white hover:bg-gray-800',
            )}
            title="Check Balance"
          >
            <Scale className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowJson(prev => !prev)}
            className={cn(
              'p-1.5 rounded transition-colors',
              showJson
                ? 'bg-green-900/40 text-green-400 border border-green-700'
                : 'text-gray-400 hover:text-white hover:bg-gray-800',
            )}
            title="Toggle JSON view"
          >
            <Code className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Mini Overview Bar ── */}
      <div className="flex-shrink-0">
        <MechanicComposerOverview config={config} onScrollToSection={scrollToSection} />
      </div>

      {/* ── Mobile View Toggle ── */}
      <div className="flex-shrink-0 lg:hidden flex border-b border-gray-700">
        <button
          onClick={() => setMobileView('edit')}
          className={cn(
            'flex-1 py-2.5 text-sm font-medium text-center transition-colors',
            mobileView === 'edit'
              ? 'text-red-400 border-b-2 border-red-500'
              : 'text-gray-400 hover:text-gray-300',
          )}
        >
          Edit
        </button>
        <button
          onClick={() => setMobileView('preview')}
          className={cn(
            'flex-1 py-2.5 text-sm font-medium text-center transition-colors flex items-center justify-center gap-1.5',
            mobileView === 'preview'
              ? 'text-red-400 border-b-2 border-red-500'
              : 'text-gray-400 hover:text-gray-300',
          )}
        >
          <Eye className="w-4 h-4" />
          Preview
        </button>
      </div>

      {/* ── Main Content: Editor + Preview ── */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Editor Panel (60% desktop, full on mobile when edit tab active) */}
        <div className={cn(
          'overflow-y-auto',
          'lg:w-3/5 lg:block lg:border-r lg:border-gray-700',
          mobileView === 'edit' ? 'w-full' : 'hidden',
        )}>
          <div className="p-4 space-y-4">
            {SECTIONS.map((section, index) => {
              const status = sectionStatuses[section.key];
              return (
                <div
                  key={section.key}
                  ref={(el) => { sectionRefs.current[index] = el; }}
                >
                  <EditorSection
                    title={section.title}
                    number={section.number}
                    status={status}
                    isExpanded={expandedSections.includes(index)}
                    onToggle={() => toggleSection(index)}
                  >
                    <section.Component
                      config={config}
                      dispatch={dispatch}
                      errors={sectionErrors[section.key]}
                    />
                  </EditorSection>
                </div>
              );
            })}
          </div>
        </div>

        {/* Preview Panel (40% desktop, full on mobile when preview tab active) */}
        <div className={cn(
          'overflow-y-auto',
          'lg:w-2/5 lg:block lg:h-full',
          mobileView === 'preview' ? 'w-full' : 'hidden',
        )}>
          <div className="p-4">
            <MechanicComposerPreview
              config={config}
              showJson={showJson}
              balanceReport={balanceReport}
              balanceLoading={balanceLoading}
              balanceError={balanceError}
              balanceStale={balanceStale}
              balanceCredits={balanceCredits}
              onRunBalanceCheck={handleBalanceCheck}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
